// FILE: src/pages/ProductsList.jsx
import React, { useState } from 'react';
import Card from '../components/ui/Card';
import DataTable from '../components/ui/DataTable';
import AddProductModal from '../components/ui/AddProductModal';

const ProductsList = () => {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [products, setProducts] = useState([]);

  // Initialize products with initial data
  React.useEffect(() => {
    if (products.length === 0) {
      setProducts(initialProductsData);
    }
  }, []);

  // Initial products data
  const initialProductsData = [
    {
      id: 1,
      name: 'Wireless Bluetooth Headphones',
      category: 'Electronics',
      price: '$129.99',
      stock: 45,
      sales: 1234,
      status: 'In Stock',
      image: '🎧',
      sku: 'WBH-001',
      rating: 4.8,
      reviews: 234
    },
    {
      id: 2,
      name: 'Smart Fitness Watch',
      category: 'Wearables',
      price: '$299.99',
      stock: 12,
      sales: 987,
      status: 'Low Stock',
      image: '⌚',
      sku: 'SFW-002',
      rating: 4.6,
      reviews: 189
    },
    {
      id: 3,
      name: 'Professional Camera Lens',
      category: 'Photography',
      price: '$899.99',
      stock: 0,
      sales: 456,
      status: 'Out of Stock',
      image: '📷',
      sku: 'PCL-003',
      rating: 4.9,
      reviews: 87
    },
    {
      id: 4,
      name: 'Ergonomic Office Chair',
      category: 'Furniture',
      price: '$349.99',
      stock: 28,
      sales: 678,
      status: 'In Stock',
      image: '🪑',
      sku: 'EOC-004',
      rating: 4.7,
      reviews: 156
    },
    {
      id: 5,
      name: 'Gaming Mechanical Keyboard',
      category: 'Gaming',
      price: '$159.99',
      stock: 67,
      sales: 890,
      status: 'In Stock',
      image: '⌨️',
      sku: 'GMK-005',
      rating: 4.5,
      reviews: 298
    },
    {
      id: 6,
      name: 'Portable Power Bank',
      category: 'Accessories',
      price: '$49.99',
      stock: 8,
      sales: 1567,
      status: 'Low Stock',
      image: '🔋',
      sku: 'PPB-006',
      rating: 4.4,
      reviews: 423
    }
  ];

  // Product statistics
  const productStats = [
    {
      title: 'Total Products',
      value: '1,247',
      change: '+5.2%',
      changeType: 'increase',
      icon: '📦'
    },
    {
      title: 'In Stock',
      value: '1,089',
      change: '+2.1%',
      changeType: 'increase',
      icon: '✅'
    },
    {
      title: 'Low Stock',
      value: '87',
      change: '+12.3%',
      changeType: 'increase',
      icon: '⚠️'
    },
    {
      title: 'Out of Stock',
      value: '71',
      change: '-8.7%',
      changeType: 'decrease',
      icon: '❌'
    }
  ];

  const productColumns = [
    {
      key: 'image',
      label: '',
      render: (value) => <span className="text-3xl">{value}</span>,
      sortable: false
    },
    {
      key: 'name',
      label: 'Product',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">SKU: {row.sku}</div>
        </div>
      )
    },
    { key: 'category', label: 'Category' },
    { key: 'price', label: 'Price' },
    {
      key: 'stock',
      label: 'Stock',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className={`text-xs ${
            row.status === 'In Stock' ? 'text-green-600' :
            row.status === 'Low Stock' ? 'text-yellow-600' :
            'text-red-600'
          }`}>
            {row.status}
          </div>
        </div>
      )
    },
    { key: 'sales', label: 'Sales' },
    {
      key: 'rating',
      label: 'Rating',
      render: (value, row) => (
        <div>
          <div className="flex items-center">
            <span className="text-yellow-400">★</span>
            <span className="ml-1 font-medium text-gray-900 dark:text-white">{value}</span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">({row.reviews} reviews)</div>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: () => (
        <div className="flex space-x-2">
          <button className="text-blue-600 hover:text-blue-700 text-sm">Edit</button>
          <button className="text-green-600 hover:text-green-700 text-sm">View</button>
          <button className="text-red-600 hover:text-red-700 text-sm">Delete</button>
        </div>
      ),
      sortable: false
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Products Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Manage your product inventory and catalog</p>
        </div>
        <div className="flex space-x-3">
          <button className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Import
          </button>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Export
          </button>
          <button 
            onClick={() => setShowAddProductModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Product
          </button>
        </div>
      </div>

      {/* Product Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {productStats.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{stat.icon}</span>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                <div className="flex items-center mt-1">
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">from last month</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search products..."
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            <div className="relative">
              <select className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option>All Categories</option>
                <option>Electronics</option>
                <option>Wearables</option>
                <option>Photography</option>
                <option>Furniture</option>
                <option>Gaming</option>
                <option>Accessories</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <div className="relative">
              <select className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option>All Status</option>
                <option>In Stock</option>
                <option>Low Stock</option>
                <option>Out of Stock</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${
                viewMode === 'list' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${
                viewMode === 'grid' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
          </div>
        </div>
      </Card>

      {/* Products List/Grid */}
      {viewMode === 'list' ? (
        <DataTable 
          columns={productColumns}
          data={products}
          searchable={false}
          pagination={true}
          itemsPerPage={10}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <Card key={product.id} className="hover:shadow-md transition-shadow">
              <div className="text-center">
                <div className="text-6xl mb-4">{product.image}</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{product.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">SKU: {product.sku}</p>
                <p className="text-sm text-blue-600 dark:text-blue-400 mb-4">{product.category}</p>
                
                <div className="flex items-center justify-center mb-3">
                  <span className="text-yellow-400 mr-1">★</span>
                  <span className="font-medium text-gray-900 dark:text-white mr-1">{product.rating}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">({product.reviews})</span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-sm mb-4">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Price</div>
                    <div className="font-semibold text-lg text-gray-900 dark:text-white">{product.price}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Stock</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{product.stock}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Sales</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{product.sales}</div>
                  </div>
                </div>

                <div className="flex justify-center mb-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    product.status === 'In Stock' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                    product.status === 'Low Stock' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                  }`}>
                    {product.status}
                  </span>
                </div>

                <div className="flex space-x-2">
                  <button className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                    Edit
                  </button>
                  <button className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500">
                    View
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <AddProductModal 
          onClose={() => setShowAddProductModal(false)}
          onAddProduct={(newProduct) => {
            const productWithId = {
              ...newProduct,
              id: products.length + 1,
              sales: 0
            };
            setProducts([...products, productWithId]);
            setShowAddProductModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ProductsList;